/**
 * A9PromoUtils — day-based promo resolution. The theme owns the amount; a SupaEasy
 * function applies it at checkout and computes nothing of its own.
 *
 * Only one discount can reach a Shopify cart line, so one function owns the whole
 * reservation line and the promo travels nested in `_membership_hours` (see attachPromoToLine).
 *
 * Windows are half-open UTC; absent facility filter = every facility; first match wins.
 * Pure half is node-testable; only utcMsForBooking needs browser globals.
 */
(function () {
  const CENTS_PER_DOLLAR = 100;

  // UTC instants — Date.parse is timezone-free and node-safe. Trim >3 fractional digits so legacy ".000000Z" stamps parse.
  function parseUtcMs(value) {
    if (typeof value !== 'string') return NaN;
    return Date.parse(value.replace(/\.(\d{3})\d+(?=Z|[+-])/, '.$1'));
  }

  // Identity-keyed: settings parse once per page, so a broken config warns once instead of per cell.
  let cachedRaw;
  let cachedPromos = [];

  /** Raw metafield value (object or JSON string) → validated promo list. */
  function normalize(raw) {
    if (raw === cachedRaw) return cachedPromos;
    cachedRaw = raw;
    cachedPromos = parseConfig(raw);
    return cachedPromos;
  }

  function parseConfig(raw) {
    if (raw == null) return [];

    let config = raw;
    if (typeof raw === 'string') {
      try {
        config = JSON.parse(raw);
      } catch (error) {
        console.warn('A9PromoUtils: promo config is not valid JSON', error);
        return [];
      }
    }

    const entries = config && config.promos;
    if (!Array.isArray(entries)) {
      if (entries != null) console.warn('A9PromoUtils: promo config has no promos array', entries);
      return [];
    }
    return entries.map(toPromo).filter(Boolean);
  }

  function toPromo(entry) {
    if (!entry) return null;

    const amount = Number(entry.amount);
    const from = parseUtcMs(entry.fromUtc);
    const to = parseUtcMs(entry.toUtc);
    if (!(amount > 0) || Number.isNaN(from) || Number.isNaN(to) || !(to > from)) {
      const hint = entry.percentage !== undefined && entry.amount === undefined
        ? ' — `percentage` is no longer supported, set `amount` in dollars instead'
        : '';
      console.warn('A9PromoUtils: skipping malformed promo' + hint, entry);
      return null;
    }

    return {
      amountCents: Math.round(amount * CENTS_PER_DOLLAR),
      from,
      to,
      facilityIds: toFacilityIds(entry.facilityIds),
      message: entry.message || '$' + amount.toFixed(2) + ' off'
    };
  }

  /** Absent/empty = every facility. Junk-only filters stay empty so they match nothing rather than widening. */
  function toFacilityIds(raw) {
    if (!Array.isArray(raw) || !raw.length) return null;
    const ids = raw.map(Number).filter(Number.isFinite);
    if (ids.length !== raw.length) console.warn('A9PromoUtils: promo has unusable facility ids', raw);
    return ids;
  }

  /** First promo covering [from, to) for this facility. Omit `toUtcMs` for a single instant. */
  function promoForRange(promos, fromUtcMs, toUtcMs, facilityId) {
    if (!Array.isArray(promos) || !Number.isFinite(fromUtcMs)) return null;

    const rangeEnd = toUtcMs === undefined || toUtcMs === null ? fromUtcMs : toUtcMs;
    const isInstant = rangeEnd === fromUtcMs;

    return promos.find((promo) => {
      const inWindow = isInstant
        ? fromUtcMs >= promo.from && fromUtcMs < promo.to
        : fromUtcMs < promo.to && rangeEnd > promo.from;
      if (!inWindow) return false;
      return !promo.facilityIds || promo.facilityIds.includes(Number(facilityId));
    }) || null;
  }

  /** Slot start if known (never falls back to day overlap); else day bounds. Non-finite = unknown. */
  function promoForBookingTimes(promos, facilityId, slotFromMs, dayFromMs, dayToMs) {
    if (Number.isFinite(slotFromMs)) return promoForRange(promos, slotFromMs, null, facilityId);
    if (Number.isFinite(dayFromMs) && Number.isFinite(dayToMs)) {
      return promoForRange(promos, dayFromMs, dayToMs, facilityId);
    }
    return null;
  }

  function discountCents(promo, subtotalCents) {
    if (!promo || !(subtotalCents > 0)) return 0;
    return Math.min(promo.amountCents, subtotalCents);
  }

  /** `subtotalCents` must already be net of membership hours. */
  function priceForBooking({ promos, facilityId, slotFromMs, dayFromMs, dayToMs, subtotalCents }) {
    const promo = promoForBookingTimes(promos, facilityId, slotFromMs, dayFromMs, dayToMs);
    if (!promo) return null;

    const offCents = discountCents(promo, subtotalCents);
    return offCents > 0 ? Object.assign({}, promo, { offCents }) : null;
  }

  /** Facility-local day (and slot Zulu once picked) → UTC ms. Unresolvable values come back non-finite. */
  function utcMsForBooking(dateYmd, timeZone, slotFromZulu) {
    const slotApi = typeof window !== 'undefined' ? window.SlotApiService : null;
    const toZulu = slotApi && slotApi.normalizeZulu ? slotApi.normalizeZulu : (value) => value;

    let bounds = null;
    if (slotApi && dateYmd && timeZone) {
      try {
        bounds = slotApi.dayBoundsZulu(dateYmd, timeZone);
      } catch (error) {
        console.warn('A9PromoUtils: could not resolve day bounds for ' + dateYmd, error);
      }
    }

    return {
      slotFromMs: slotFromZulu ? parseUtcMs(toZulu(slotFromZulu)) : NaN,
      dayFromMs: bounds ? parseUtcMs(toZulu(bounds.from)) : NaN,
      dayToMs: bounds ? parseUtcMs(toZulu(bounds.to)) : NaN
    };
  }

  function resolveForBooking({ promosRaw, facilityId, dateYmd, timeZone, slotFromZulu, subtotalCents }) {
    if (!facilityId || !(subtotalCents > 0)) return null;

    const promos = normalize(promosRaw);
    if (!promos.length) return null;

    const times = utcMsForBooking(dateYmd, timeZone, slotFromZulu);
    return priceForBooking({ promos, facilityId, subtotalCents, ...times });
  }

  /** Fields a SupaEasy function needs to apply the promo. */
  function promoPayload(pricedPromo) {
    if (!pricedPromo || !(pricedPromo.offCents > 0)) return null;
    return {
      message: pricedPromo.message,
      amount: (pricedPromo.offCents / CENTS_PER_DOLLAR).toFixed(2)
    };
  }

  /**
   * Nest the promo in `_membership_hours`. Shopify applies one discount per line, so one
   * SupaEasy function owns the whole reservation line — hours, tier percent and promo — and
   * the promo travels inside the property that function already reads. Mutates the objects
   * given; `membershipProperty` must be the same object written to `_membership_hours`.
   *
   * Every reservation line carries that property, including for a non-member: the popup and
   * the multi-suite controller both write a zero-valued one. Without a carrier the promo has
   * nowhere to go, so that case warns instead of quietly dropping money.
   *
   * `pricedAfterMembership` must match whether the property will actually discount — a
   * mismatch attaches nothing rather than charging a figure the customer was never quoted.
   */
  function attachPromoToLine(properties, membershipProperty, pricedPromo) {
    const payload = promoPayload(pricedPromo);
    if (!payload || !properties) return null;

    // Hours reduce the line whether a membership or an hour credit pays them; credit may ride
    // its own `_hour_credit` property, so the membership carrier alone cannot answer this.
    const credit = typeof window !== 'undefined' ? window.A9HourCredit : null;
    const membershipWillDiscount = !!(membershipProperty
      && membershipProperty.status === 'success'
      && Number(membershipProperty.discount) > 0)
      || !!(credit && credit.lineHasCreditDiscount(properties));

    if (pricedPromo.pricedAfterMembership !== membershipWillDiscount) {
      console.warn('A9PromoUtils: promo priced for a different hours outcome, not attaching', {
        pricedAfterMembership: pricedPromo.pricedAfterMembership,
        membershipWillDiscount
      });
      return null;
    }

    if (!membershipProperty) {
      console.warn('A9PromoUtils: no _membership_hours carrier on the line, promo not attached', payload);
      return null;
    }

    membershipProperty.promo = payload;
    return 'membership';
  }

  const api = {
    normalize,
    promoForRange,
    promoForBookingTimes,
    discountCents,
    priceForBooking,
    utcMsForBooking,
    resolveForBooking,
    promoPayload,
    attachPromoToLine
  };
  if (typeof window !== 'undefined') window.A9PromoUtils = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
